import { createFileRoute, useNavigate, useBlocker } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { TackCard } from "@/components/TackCard";
import { TackJourneyPreview } from "@/components/TackJourneyPreview";
import { BusinessCard } from "@/components/BusinessCard";
import { PosterCard } from "@/components/PosterCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { track } from "@/lib/track";
import { trackPlausible } from "@/lib/plausible";
import { QRCodeSVG } from "qrcode.react";
import { Flame, FileText } from "lucide-react";
import { ensureMerchantFn } from "@/lib/merchant-bootstrap.functions";
import { withAuth } from "@/lib/server-fn-auth";
import { RewardQuotaPicker } from "@/components/RewardQuotaPicker";
import { validateBookingUrl } from "@/lib/booking-url";
import { notifyBookingUrlPendingFn } from "@/lib/admin-notify.functions";
import {
  ARCHETYPES,
  CURRENCIES,
  CURRENCY_SYMBOLS,
  LOCALE_LABELS,
  LOCALE_GROUPS,
  LOCALE_DEFAULTS,
  NUDGE_LEAD_OPTIONS,

  detectLocale,
  getLocaleDefaults,
  type BusinessType,
  type Currency,
} from "@/lib/onboarding-defaults";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authenticated/onboarding")({
  validateSearch: (search: Record<string, unknown>) => ({
    rerun: search.rerun === true || search.rerun === "true" ? true : undefined,
  }),
  component: OnboardingPage,
});

type Step = 1 | 2 | 3;

function OnboardingPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { rerun } = Route.useSearch();
  const isRerun = rerun === true;
  const [merchantId, setMerchantId] = useState<string | null>(null);
  const [slug, setSlug] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(true);

  // Step 1 fields
  const [businessType, setBusinessType] = useState<BusinessType | null>(null);
  const [avgOrderValue, setAvgOrderValue] = useState(500);
  const [currency, setCurrency] = useState<Currency>("SEK");
  const [discountPercent, setDiscountPercent] = useState(10);
  const [nudgeLeadDays, setNudgeLeadDays] = useState(14);
  const [rewardExpiryMonths, setRewardExpiryMonths] = useState(3);
  const [promoActivationHours, setPromoActivationHours] = useState(24);
  const [activationTouched, setActivationTouched] = useState(false);
  const [redemptionStyle, setRedemptionStyle] = useState<"quick_burn" | "verified_voucher">("quick_burn");
  const [rewardQuotaCount, setRewardQuotaCount] = useState(2);
  const [rewardQuotaWindowDays, setRewardQuotaWindowDays] = useState(365);
  const [bookingUrl, setBookingUrl] = useState("");
  const [bookingUrlError, setBookingUrlError] = useState<string | null>(null);
  const [includeBookingUrl, setIncludeBookingUrl] = useState(true);

  // Step 2 fields
  const [selectedLocale, setSelectedLocale] = useState(detectLocale());
  const [tagline, setTagline] = useState("");
  const [welcomeText, setWelcomeText] = useState("How was your experience?");
  const [feedbackPrompt, setFeedbackPrompt] = useState("Any thoughts on your experience?");
  const [emailLabel, setEmailLabel] = useState("Your email");
  const [ctaText, setCtaText] = useState("Get 10% Off");
  const [thankYouText, setThankYouText] = useState("Tack! 🎉");
  const [discountCodeLabel, setDiscountCodeLabel] = useState("Your discount code");
  const [discountSubtext, setDiscountSubtext] = useState("10% off your next visit");
  const [redeemButtonText, setRedeemButtonText] = useState("Redeem at Register");
  const [saveCodeHint, setSaveCodeHint] = useState("Or save this code for your next visit");
  const [activatesInText, setActivatesInText] = useState("Your code activates in");
  const [emailWhenReadyText, setEmailWhenReadyText] = useState("We'll email you when your 10% discount is ready!");
  const [feedbackOptional, setFeedbackOptional] = useState("Optional");
  const [showCashierText, setShowCashierText] = useState("Show this to the cashier");
  const [checkInboxText, setCheckInboxText] = useState("Check your inbox — we'll send your code when it's active");
  const [submittedSentText, setSubmittedSentText] = useState("We've sent a {X}% discount code to your email.");
  const [submittedClaimText, setSubmittedClaimText] = useState("Open the link in your inbox to claim it.");
  const [submittedSpamText, setSubmittedSpamText] = useState("Don't forget to check your spam folder");
  // previewStep removed — all 5 shown simultaneously

  // Step 3 fields
  const [posterTopText, setPosterTopText] = useState("for today");
  const [posterBottomText, setPosterBottomText] = useState("Here is a gift for next time!");
  const [cardLine1, setCardLine1] = useState("Here is a gift for next time!");
  const [cardLine2, setCardLine2] = useState("for today");

  const [saving, setSaving] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const bookingUrlRef = useRef<HTMLInputElement>(null);
  const detectedLocale = useRef(detectLocale());
  const bypassBlockRef = useRef(false);

  // Snapshot the current step's fields; when they diverge from the snapshot
  // taken on step entry the step is "dirty" and navigating away should warn.
  const stepSnapshot = JSON.stringify(
    step === 1
      ? { businessType, avgOrderValue, currency, discountPercent, nudgeLeadDays, rewardExpiryMonths, promoActivationHours, redemptionStyle, rewardQuotaCount, rewardQuotaWindowDays, bookingUrl, includeBookingUrl }
      : step === 2
      ? { selectedLocale, tagline, welcomeText, feedbackPrompt, emailLabel, ctaText, thankYouText, discountCodeLabel, discountSubtext, redeemButtonText, saveCodeHint, activatesInText, emailWhenReadyText, feedbackOptional, showCashierText, checkInboxText, submittedSentText, submittedClaimText, submittedSpamText }
      : { posterTopText, posterBottomText, cardLine1, cardLine2 }
  );
  const initialSnapshotRef = useRef<string>("");
  useEffect(() => {
    initialSnapshotRef.current = stepSnapshot;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, loading]);
  const isDirty = !loading && initialSnapshotRef.current !== "" && initialSnapshotRef.current !== stepSnapshot;

  const blocker = useBlocker({
    shouldBlockFn: () => !bypassBlockRef.current && isDirty,
    enableBeforeUnload: () => isDirty,
    withResolver: true,
  });


  // Apply locale defaults with current discount percent
  const applyLocale = (locale: string, pct?: number) => {
    const d = getLocaleDefaults(locale);
    const p = pct ?? discountPercent;
    setWelcomeText(d.welcomeText);
    setFeedbackPrompt(d.feedbackPrompt);
    setEmailLabel(d.emailLabel);
    setCtaText(d.getDiscountCta.replace("{X}", String(p)));
    setThankYouText(d.thankYou);
    setDiscountCodeLabel(d.discountCodeLabel);
    setDiscountSubtext(d.discountSubtext.replace("{X}", String(p)));
    setRedeemButtonText(d.redeemButton);
    setSaveCodeHint(d.saveCodeHint);
    setActivatesInText(d.activatesIn);
    setEmailWhenReadyText(d.emailWhenReady.replace("{X}", String(p)));
    setFeedbackOptional(d.feedbackOptional);
    setShowCashierText(d.showCashier);
    setCheckInboxText(d.checkInbox);
    setSubmittedSentText(d.submittedSent.replace("{X}", String(p)));
    setSubmittedClaimText(d.submittedClaim);
    setSubmittedSpamText(d.submittedSpam);
    // Also update step 3 fields (respect UI caps: 20 / 40)
    setPosterTopText(d.posterTop.slice(0, 20));
    setPosterBottomText(d.posterBottom.slice(0, 40));
    setCardLine1(d.cardLine1.slice(0, 40));
    setCardLine2(d.cardLine2.slice(0, 20));
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("merchants")
        .select("id, slug, business_name, onboarding_completed")
        .eq("user_id", user.id)
        .maybeSingle();

      if (cancelled) return;

      if (data) {
        // A completed merchant only enters the wizard through an explicit
        // re-run (Settings → "Run onboarding again"). Re-runs never clear the
        // completed flag, so exiting midway can't strand the merchant here.
        if (data.onboarding_completed && !isRerun) {
          navigate({ to: "/dashboard" });
          return;
        }

        setMerchantId(data.id);
        setSlug(data.slug);
        setBusinessName(data.business_name);
        applyLocale(detectedLocale.current);
        setLoading(false);
        return;
      }

      // Self-heal: signup race left no merchant row. Create one server-side.
      // Retry once on transient failure (cold session right after signup).
      try {
        let res = await withAuth(ensureMerchantFn)({ data: { businessName: undefined } });
        if (!res.ok) {
          console.warn("[onboarding] first ensureMerchantFn failed, retrying:", res.error);
          await new Promise((r) => setTimeout(r, 400));
          res = await withAuth(ensureMerchantFn)({ data: { businessName: undefined } });
        }
        if (cancelled) return;
        if (res.ok) {
          const { data: m } = await supabase
            .from("merchants")
            .select("id, slug, business_name, onboarding_completed")
            .eq("id", res.merchantId)
            .maybeSingle();
          if (cancelled) return;
          if (m) {
            setMerchantId(m.id);
            setSlug(m.slug);
            setBusinessName(m.business_name);
            applyLocale(detectedLocale.current);
          } else {
            console.error("ensureMerchantFn returned ok but merchant fetch came back empty");
            toast.error(t('onboarding.setupError'));
          }
        } else {
          toast.error(t('onboarding.setupError'));
          console.error("ensureMerchantFn failed twice:", res.error);
        }
      } catch (err) {
        console.error("ensureMerchantFn threw:", err);
        toast.error(t('onboarding.setupError'));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, navigate, isRerun]);

  // Voucher (approval) mode has no reason to hold the code back — the merchant
  // already approved the visit. Quick burn keeps the 24h default.
  const selectRedemptionStyle = (style: "quick_burn" | "verified_voucher") => {
    setRedemptionStyle(style);
    if (!activationTouched) {
      setPromoActivationHours(style === "verified_voucher" ? 0 : 24);
    }
  };

  const selectArchetype = (type: BusinessType) => {
    setBusinessType(type);
    const preset = ARCHETYPES[type];
    setDiscountPercent(preset.discountPercent);
    setNudgeLeadDays(preset.nudgeLeadDays);
    setRewardExpiryMonths(preset.rewardExpiryMonths);
    setAvgOrderValue(preset.avgOrderValue);
    setRewardQuotaCount(preset.rewardQuotaCount);
    setRewardQuotaWindowDays(preset.rewardQuotaWindowDays);
    selectRedemptionStyle(preset.redemptionStyle);
  };

  const saveStep1 = async () => {
    if (!merchantId) return;
    let bookingUrlValue: string | null = null;
    if (includeBookingUrl) {
      const validation = validateBookingUrl(bookingUrl, { required: true });
      if (!validation.ok) {
        const msg =
          validation.errorKey === "empty"
            ? t("onboarding.bookingUrlRequired")
            : t("onboarding.bookingUrlInvalid");
        setBookingUrlError(msg);
        toast.error(t("onboarding.bookingUrlToastTitle"), {
          description: t("onboarding.bookingUrlToastBody"),
        });
        bookingUrlRef.current?.focus();
        bookingUrlRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      setBookingUrl(validation.value);
      bookingUrlValue = validation.value;
    }
    setBookingUrlError(null);
    setSaving(true);
    // Re-apply locale with new discount percent
    applyLocale(selectedLocale, discountPercent);
    await supabase.from("merchants").update({
      business_type: businessType,
      avg_order_value: avgOrderValue,
      currency,
      promo_discount_percent: discountPercent,
      followup_discount_percent: discountPercent,
      nudge_lead_days: nudgeLeadDays,
      nudge_enabled: true,
      reward_expiry_months: rewardExpiryMonths,
      promo_activation_hours: promoActivationHours,
      redemption_style: redemptionStyle,
      reward_quota_count: rewardQuotaCount,
      reward_quota_window_days: rewardQuotaWindowDays,
      locale: selectedLocale,
      booking_url: bookingUrlValue,
    } as any).eq("id", merchantId);
    if (bookingUrlValue) {
      try {
        await withAuth(notifyBookingUrlPendingFn)({ data: { merchantId } });
      } catch (err) {
        console.warn("[onboarding] booking URL notification failed", err);
      }
    }
    setSaving(false);
    track("onboarding_step", merchantId, { step: 1, business_type: businessType, redemption_style: redemptionStyle });
    setStep(2);
  };

  const saveStep2 = async () => {
    if (!merchantId) return;
    setSaving(true);
    await supabase.from("merchants").update({
      locale: selectedLocale,
      brand_tagline: tagline || null,
      brand_welcome_text: welcomeText || null,
      feedback_prompt: feedbackPrompt || null,
      email_label: emailLabel || null,
      cta_text: ctaText || null,
      thank_you_text: thankYouText || null,
      discount_code_label: discountCodeLabel || null,
      discount_subtext: discountSubtext || null,
      redeem_button_text: redeemButtonText || null,
      save_code_hint: saveCodeHint || null,
      activates_in_text: activatesInText || null,
      email_when_ready_text: emailWhenReadyText || null,
      feedback_optional_text: feedbackOptional || null,
      show_cashier_text: showCashierText || null,
      check_inbox_text: checkInboxText || null,
      valid_until_text: null,
    } as any).eq("id", merchantId);
    setSaving(false);
    track("onboarding_step", merchantId, { step: 2 });
    setStep(3);
  };

  const finishOnboarding = async () => {
    if (!merchantId) return;
    setSaving(true);
    await supabase.from("merchants").update({
      poster_top_text: posterTopText,
      poster_bottom_text: posterBottomText,
      card_line1: cardLine1,
      card_line2: cardLine2,
      onboarding_completed: true,
      onboarding_completed_at: new Date().toISOString(),
    }).eq("id", merchantId);
    setSaving(false);
    track("onboarding_step", merchantId, { step: 3, completed: true });
    trackPlausible("Onboarding Completed");

    // Trigger welcome email — awaited with a short timeout so the request is
    // actually issued before navigation, but a slow backend never strands the user.
    try {
      const invoke = supabase.functions.invoke("send-welcome-email", {
        body: { merchant_id: merchantId },
      });
      const timeout = new Promise((resolve) => setTimeout(() => resolve({ timedOut: true }), 4000));
      const result: any = await Promise.race([invoke, timeout]);
      if (result?.timedOut) {
        console.warn("Welcome email invoke timed out — request was issued but ack not received");
      } else if (result?.error) {
        console.error("Welcome email invoke failed:", result.error);
      }
    } catch (e) {
      console.error("Welcome email trigger error:", e);
    }

    toast.success(t('onboarding.allSet'));
    bypassBlockRef.current = true;
    navigate({ to: "/poster/$slug", params: { slug }, search: { welcome: true, starter_kit: undefined } });

  };

  const feedbackUrl = typeof window !== "undefined" ? `${window.location.origin}/tack/${slug}` : `/tack/${slug}`;
  const primaryColor = "hsl(20, 55%, 60%)";
  const showLocaleHint = detectedLocale.current !== "en";
  const localeName: Record<string, string> = { sv: "Swedish", no: "Norwegian", da: "Danish", de: "German", fr: "French" };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t('onboarding.loading')}</div>
      </div>
    );
  }

  // Preview labels come from the shared <TackJourneyPreview> component
  // so onboarding step 2 and the Tack-preview page stay in lock-step.


  return (
    <>
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {isRerun && (
          <div className="flex justify-end mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: "/dashboard" })}
            >
              {t("onboarding.exitToDashboard")}
            </Button>
          </div>
        )}
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors"
                style={{
                  backgroundColor: step >= s ? primaryColor : "transparent",
                  color: step >= s ? "#fff" : primaryColor,
                  border: `2px solid ${primaryColor}`,
                }}
              >
                {s}
              </div>
              {s < 3 && <div className="w-12 h-0.5" style={{ backgroundColor: step > s ? primaryColor : "#e5e5e5" }} />}
            </div>
          ))}
        </div>

        {/* Step 1: Business Profile */}
        {step === 1 && (
          <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
            <div className="text-center">
              <h1 className="text-2xl font-display font-bold">{t('onboarding.step1Title')}</h1>
              <p className="text-muted-foreground mt-1 text-sm">{t('onboarding.step1Subtitle')}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {(Object.entries(ARCHETYPES) as [BusinessType, typeof ARCHETYPES.hair][]).map(([key, arch]) => (
                <button
                  key={key}
                  onClick={() => selectArchetype(key)}
                  className="text-left rounded-xl p-4 transition-all border-2"
                  style={{
                    borderColor: businessType === key ? primaryColor : "#e5e5e5",
                    backgroundColor: businessType === key ? "rgba(212, 123, 94, 0.06)" : "#fff",
                  }}
                >
                  <span className="text-2xl">{arch.icon}</span>
                  <p className="font-semibold mt-2 text-sm">{t(`onboarding.archetypes.${key}.label`, arch.label)}</p>
                  <p className="text-xs text-muted-foreground">{t(`onboarding.archetypes.${key}.subtitle`, arch.subtitle)}</p>
                </button>
              ))}
            </div>

            {businessType && (
              <div className="space-y-5 animate-fade-in">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t('onboarding.avgOrderValue')}</Label>
                    <Input type="number" value={avgOrderValue} onChange={(e) => setAvgOrderValue(Number(e.target.value))} className="h-8 text-sm" />
                    <p className="text-[10px] text-muted-foreground">{t('onboarding.avgOrderHint')}</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t('onboarding.currency')}</Label>
                    <select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)} className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 text-sm">
                      {CURRENCIES.map((c) => (
                        <option key={c} value={c}>{c} ({CURRENCY_SYMBOLS[c]})</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div
                  className={`space-y-2 rounded-lg border p-3 bg-muted/20 transition-colors ${
                    bookingUrlError ? "border-destructive ring-1 ring-destructive/40" : "border-input"
                  }`}
                >
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeBookingUrl}
                      onChange={(e) => {
                        setIncludeBookingUrl(e.target.checked);
                        if (!e.target.checked) setBookingUrlError(null);
                      }}
                      className="mt-0.5 h-4 w-4 rounded border-input accent-primary cursor-pointer"
                    />
                    <div className="space-y-0.5">
                      <div className="text-xs font-medium">
                        {t('onboarding.bookingUrlToggle')}{' '}
                        <span className="text-primary font-normal">{t('onboarding.bookingUrlToggleBadge')}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">{t('onboarding.bookingUrlToggleHint')}</p>
                    </div>
                  </label>
                  {includeBookingUrl && (
                    <div className="space-y-1.5 pt-1">
                      <Input
                        ref={bookingUrlRef}
                        type="url"
                        inputMode="url"
                        value={bookingUrl}
                        onChange={(e) => {
                          setBookingUrl(e.target.value);
                          if (bookingUrlError) setBookingUrlError(null);
                        }}
                        placeholder={t('onboarding.bookingUrlPlaceholder')}
                        className={`h-8 text-sm ${bookingUrlError ? "border-destructive focus-visible:ring-destructive" : ""}`}
                        aria-invalid={bookingUrlError ? true : undefined}
                      />
                      <p className="text-[10px] text-muted-foreground">{t('onboarding.bookingUrlHelper')}</p>
                      <p className="text-[10px] text-tack-amber">{t('onboarding.bookingUrlReviewNote')}</p>
                      {bookingUrlError && (
                        <p className="text-[11px] text-destructive">{bookingUrlError}</p>
                      )}
                    </div>
                  )}
                </div>


                <div className="rounded-lg border border-tack-amber/30 bg-tack-amber/5 p-4">
                  <p className="text-sm font-medium mb-1">{t('onboarding.launchTitle')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('onboarding.launchBody')}
                  </p>
                </div>

                {/* Redemption Style picker */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t('onboarding.howRedeem')}</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => selectRedemptionStyle("quick_burn")}
                      className="text-left rounded-lg p-3 transition-all border-2"
                      style={{
                        borderColor: redemptionStyle === "quick_burn" ? primaryColor : "#e5e5e5",
                        backgroundColor: redemptionStyle === "quick_burn" ? "rgba(212, 123, 94, 0.06)" : "#fff",
                      }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Flame size={16} style={{ color: primaryColor }} />
                        <span className="font-semibold text-sm">{t('onboarding.quickBurn')}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">{t('onboarding.quickBurnDesc')}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => selectRedemptionStyle("verified_voucher")}
                      className="text-left rounded-lg p-3 transition-all border-2"
                      style={{
                        borderColor: redemptionStyle === "verified_voucher" ? primaryColor : "#e5e5e5",
                        backgroundColor: redemptionStyle === "verified_voucher" ? "rgba(212, 123, 94, 0.06)" : "#fff",
                      }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <FileText size={16} style={{ color: primaryColor }} />
                        <span className="font-semibold text-sm">{t('onboarding.verifiedVoucher')}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">{t('onboarding.verifiedVoucherDesc')}</p>
                    </button>
                  </div>
                </div>

                <Card className="border-dashed">
                  <CardContent className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t('onboarding.discountPct')}</Label>
                        <div className="flex items-center gap-3">
                          <Slider value={[discountPercent]} onValueChange={([v]) => setDiscountPercent(v)} min={3} max={30} step={1} className="flex-1" />
                          <span className="text-sm font-semibold w-8 text-right">{discountPercent}%</span>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t('onboarding.rewardValid')}</Label>
                        <select
                          value={rewardExpiryMonths}
                          onChange={(e) => setRewardExpiryMonths(Number(e.target.value))}
                          className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                        >
                          <option value={0}>{t('onboarding.noExpiry')}</option>
                          <option value={1}>{t('onboarding.months1')}</option>
                          <option value={3}>{t('onboarding.months3')}</option>
                          <option value={6}>{t('onboarding.months6')}</option>
                          <option value={12}>{t('onboarding.months12')}</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t('onboarding.smsReminder')}</Label>
                      <select
                        value={nudgeLeadDays}
                        onChange={(e) => setNudgeLeadDays(Number(e.target.value))}
                        disabled={rewardExpiryMonths === 0}
                        className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 text-sm disabled:opacity-50"
                      >
                        {NUDGE_LEAD_OPTIONS.map((d) => (
                          <option key={d} value={d}>
                            {t(`onboarding.nudgeLeadLabel.${d}`, { defaultValue: `${d}d` })}
                          </option>
                        ))}

                      </select>
                      {rewardExpiryMonths === 0 && (
                        <p className="text-[10px] text-muted-foreground">{t('onboarding.reminderNeedsValidity')}</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t('onboarding.activationDelay')}</Label>
                      <div className="flex items-center gap-3">
                        <Slider value={[promoActivationHours]} onValueChange={([v]) => { setActivationTouched(true); setPromoActivationHours(v); }} min={0} max={72} step={1} className="flex-1" />
                        <span className="text-sm font-semibold w-12 text-right">{promoActivationHours}h</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        {promoActivationHours === 0 ? t('onboarding.activationImmediate') : t('onboarding.activationHold', { hours: promoActivationHours })}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Reward frequency (open by default) */}
                <details className="rounded-lg border border-border bg-background" open>
                  <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium flex items-center justify-between gap-3">
                    <span>{t('onboarding.rewardFrequency')}</span>
                    <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-3 py-1 text-sm font-semibold tabular-nums">
                      {rewardQuotaCount === 0
                        ? t('onboarding.picker.unlimited')
                        : `${rewardQuotaCount} / ${t(`onboarding.windowLabel.${rewardQuotaWindowDays}`, { defaultValue: `${rewardQuotaWindowDays}d` })}`}
                    </span>
                  </summary>
                  <div className="px-4 pb-4">
                    {businessType === "other" && (
                      <p className="text-xs text-muted-foreground mb-3">
                        {t('onboarding.adjustFreqHint')}
                      </p>
                    )}
                    <RewardQuotaPicker
                      count={rewardQuotaCount}
                      windowDays={rewardQuotaWindowDays}
                      onChange={(c, w) => {
                        setRewardQuotaCount(c);
                        setRewardQuotaWindowDays(w);
                      }}
                    />
                  </div>
                </details>


                <Button onClick={saveStep1} disabled={saving || !merchantId} className="w-full" size="lg">
                  {saving ? t('onboarding.saving') : !merchantId ? t('onboarding.settingUp') : t('onboarding.nextCustomize')}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Tack Page Preview */}
        {step === 2 && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center">
              <h1 className="text-2xl font-display font-bold">{t('onboarding.step2Title')}</h1>
              <p className="text-muted-foreground mt-1 text-sm">{t('onboarding.step2Subtitle')}</p>
            </div>

            {/* Language pack + locale hint */}
            <div className="flex items-center gap-3">
              <div className="flex-1">
                {showLocaleHint ? (
                  <div className="rounded-lg border border-tack-amber/30 bg-tack-amber/5 p-3 text-center">
                    <p className="text-sm">
                      <Trans i18nKey="onboarding.detectedLocale" values={{ locale: localeName[detectedLocale.current] || detectedLocale.current }} components={{ 1: <strong /> }} />
                    </p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-muted bg-muted/30 p-3 text-center">
                    <p className="text-xs text-muted-foreground italic">
                      {t('onboarding.translateTip')}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex-shrink-0">
                <Label className="text-[10px] text-muted-foreground block mb-0.5">{t('onboarding.language')}</Label>
                <select
                  value={selectedLocale}
                  onChange={(e) => {
                    setSelectedLocale(e.target.value);
                    applyLocale(e.target.value);
                  }}
                  className="flex h-8 rounded-md border border-input bg-transparent px-2 text-xs"
                >
                  {LOCALE_GROUPS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.codes.map((code) => (
                        <option key={code} value={code}>{LOCALE_LABELS[code]}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>

            {/* Edit fields for the Form screen only */}
            <Card className="border-dashed">
              <CardContent className="p-4 space-y-3">
                <p className="text-xs text-muted-foreground italic">{t('onboarding.editFieldsHint')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">{t('onboarding.welcomeText')}</Label>
                    <Input value={welcomeText} onChange={(e) => e.target.value.length <= 60 && setWelcomeText(e.target.value)} className="h-8 text-xs" maxLength={60} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('onboarding.feedbackPrompt')}</Label>
                    <Input value={feedbackPrompt} onChange={(e) => e.target.value.length <= 80 && setFeedbackPrompt(e.target.value)} className="h-8 text-xs" maxLength={80} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* All 5 previews side-by-side — shared component with Tack-preview page */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">{t('onboarding.journeyPreview')}</p>
              <TackJourneyPreview
                merchant={{
                  business_name: businessName,
                  promo_discount_percent: discountPercent,
                  promo_activation_hours: promoActivationHours,
                  brand_tagline: tagline || null,
                  brand_welcome_text: welcomeText || null,
                  feedback_prompt: feedbackPrompt || null,
                  booking_url: includeBookingUrl ? bookingUrl : null,
                  // Not yet admin-approved during onboarding, but the preview
                  // shows what to expect from the link the merchant entered.
                  booking_url_status: null,
                  sms_optout_code: null,
                  reward_expiry_months: rewardExpiryMonths,
                }}
                locale={selectedLocale}
                redemptionStyle={redemptionStyle}
              />
            </div>


            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">{t('onboarding.back')}</Button>
              <Button onClick={saveStep2} disabled={saving} className="flex-1" size="lg">
                {saving ? t('onboarding.saving') : t('onboarding.nextPoster')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Poster & Cards */}
        {step === 3 && (
          <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
            <div className="text-center">
              <h1 className="text-2xl font-display font-bold">{t('onboarding.step3Title')}</h1>
              <p className="text-muted-foreground mt-1 text-sm">{t('onboarding.step3Subtitle')}</p>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1">
                {showLocaleHint ? (
                  <div className="rounded-lg border border-tack-amber/30 bg-tack-amber/5 p-3 text-center">
                    <p className="text-sm">
                      <Trans i18nKey="onboarding.detectedLocale" values={{ locale: localeName[detectedLocale.current] || detectedLocale.current }} components={{ 1: <strong /> }} />
                    </p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-muted bg-muted/30 p-3 text-center">
                    <p className="text-xs text-muted-foreground italic">
                      {t('onboarding.translateTip')}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex-shrink-0">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <Label className="text-[10px] text-muted-foreground">{t('onboarding.language')}</Label>
                  <button
                    type="button"
                    onClick={() => setShowResetConfirm(true)}
                    className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                  >
                    {t('onboarding.resetDefaults')}
                  </button>
                </div>
                <select
                  value={selectedLocale}
                  onChange={(e) => {
                    setSelectedLocale(e.target.value);
                    const d = getLocaleDefaults(e.target.value);
                    setPosterTopText(d.posterTop.slice(0, 20));
                    setPosterBottomText(d.posterBottom.slice(0, 40));
                    setCardLine1(d.cardLine1.slice(0, 40));
                    setCardLine2(d.cardLine2.slice(0, 20));
                  }}
                  className="flex h-8 rounded-md border border-input bg-transparent px-2 text-xs"
                >
                  {LOCALE_GROUPS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.codes.map((code) => (
                        <option key={code} value={code}>{LOCALE_LABELS[code]}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Poster fields + preview */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold" style={{ color: primaryColor }}>{t('onboarding.a4Poster')}</h3>
                <div className="space-y-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <Label className="text-xs">{t('onboarding.headerSubtitle')}</Label>
                    <span className="text-[10px] text-muted-foreground tabular-nums">{posterTopText.length}/20</span>
                  </div>
                  <Input value={posterTopText} onChange={(e) => e.target.value.length <= 20 && setPosterTopText(e.target.value)} className="h-8 text-xs" maxLength={20} />
                </div>
                <div className="space-y-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <Label className="text-xs">{t('onboarding.callToAction')}</Label>
                    <span className="text-[10px] text-muted-foreground tabular-nums">{posterBottomText.length}/40</span>
                  </div>
                  <Input value={posterBottomText} onChange={(e) => e.target.value.length <= 40 && setPosterBottomText(e.target.value)} className="h-8 text-xs" maxLength={40} />
                </div>
                {/* A4 poster preview using real PosterCard component */}
                <div className="flex justify-center">
                  <div className="overflow-hidden" style={{ width: 595 * 0.30, height: 842 * 0.30 }}>
                    <div style={{ transform: "scale(0.30)", transformOrigin: "top left", width: 595, height: 842 }}>
                      <PosterCard
                        primaryColor={primaryColor}
                        feedbackUrl={feedbackUrl}
                        topText={posterTopText}
                        bottomText={posterBottomText}
                        merchantName={businessName}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Card fields + preview */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold" style={{ color: primaryColor }}>{t('onboarding.businessCard')}</h3>
                <div className="space-y-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <Label className="text-xs">{t('onboarding.headerSubtitle')}</Label>
                    <span className="text-[10px] text-muted-foreground tabular-nums">{cardLine2.length}/20</span>
                  </div>
                  <Input value={cardLine2} onChange={(e) => e.target.value.length <= 20 && setCardLine2(e.target.value)} className="h-8 text-xs" maxLength={20} />
                </div>
                <div className="space-y-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <Label className="text-xs">{t('onboarding.callToAction')}</Label>
                    <span className="text-[10px] text-muted-foreground tabular-nums">{cardLine1.length}/40</span>
                  </div>
                  <Input value={cardLine1} onChange={(e) => e.target.value.length <= 40 && setCardLine1(e.target.value)} className="h-8 text-xs" maxLength={40} />
                </div>
                {/* Business card proportional preview (85:55) */}
                <div className="flex justify-center">
                  <div className="overflow-hidden" style={{ width: 221, height: 143 }}>
                    <div style={{ transform: "scale(0.433)", transformOrigin: "top left", width: 510, height: 330 }}>
                      <BusinessCard
                        primaryColor={primaryColor}
                        feedbackUrl={feedbackUrl}
                        line1={cardLine1}
                        line2={cardLine2}
                        merchantName={businessName}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1">{t('onboarding.back')}</Button>
              <Button
                onClick={finishOnboarding}
                disabled={saving}
                className="flex-1"
                size="lg"
                style={{ backgroundColor: primaryColor }}
              >
                {saving ? t('onboarding.finishingUp') : t('onboarding.finish')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>

    <AlertDialog open={blocker.status === "blocked"}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('onboarding.unsavedTitle')}</AlertDialogTitle>
          <AlertDialogDescription>{t('onboarding.unsavedBody')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => blocker.reset?.()}>{t('onboarding.unsavedStay')}</AlertDialogCancel>
          <AlertDialogAction onClick={() => blocker.proceed?.()}>{t('onboarding.unsavedLeave')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('onboarding.resetTitle', { locale: LOCALE_LABELS[selectedLocale] || selectedLocale })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('onboarding.resetBody', { locale: LOCALE_LABELS[selectedLocale] || selectedLocale })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('onboarding.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              const d = getLocaleDefaults(selectedLocale);
              setPosterTopText(d.posterTop.slice(0, 20));
              setPosterBottomText(d.posterBottom.slice(0, 40));
              setCardLine1(d.cardLine1.slice(0, 40));
              setCardLine2(d.cardLine2.slice(0, 20));
              setShowResetConfirm(false);
            }}
          >
            {t('onboarding.reset')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
